import { ChatService } from './chat.service';
import { WebsocketService } from './websocket.service';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { computed, EventEmitter, Injectable, Output, Signal, signal, WritableSignal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CustomCookieServiceService } from './custom-cookie-service.service';
import { EMPTY, firstValueFrom, Observable, of, Subject, throwError } from 'rxjs';
import { catchError, distinctUntilChanged, filter, map, tap } from 'rxjs/operators';
import { UserRole, BypassAPIGlobalHandleEnums } from '../constants';
import { EmailCheckResponse, SocialSignInResponse, User } from '../models';
import { UtilityService } from './utility.service';
import { TranslateService } from '@ngx-translate/core';
import { Location } from '@angular/common';
import { Router } from '@angular/router';
import { UserObj } from '../microservice-clients/user';
import { ApiService } from './api.service';

export type AuthStatus = 'loading' | 'anonymous' | 'authenticated';

export interface Session {
  user: User;
  details: UserObj;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {

  private readonly userSignal = signal<User | null>(null);
  private readonly refreshing = signal(false);

  /** JWT user. Set synchronously as soon as an access token is known. */
  get user(): User {
    return this.userSignal();
  }
  set user(value: User) {
    this.userSignal.set(value);
  }

  readonly userState: WritableSignal<UserObj | null> = signal(null);
  //REMOVE THIS WHEN USERDETAILS ARE REFACTORED TO SIGNALS

  /**
   * 'loading'       – a JWT user exists but its userDetails have not arrived yet, or a token refresh is in flight
   * 'anonymous'     – nobody is logged in
   * 'authenticated' – both the JWT user and the matching userDetails are available
   */
  readonly authStatus: Signal<AuthStatus> = computed(() => {
    const user = this.userSignal();
    const details = this.userState();
    if (!user) {
      return this.refreshing() ? 'loading' : 'anonymous';
    }
    // JWT id is a number, UserObj id is a string
    return details && String(details.id) === String(user.id) ? 'authenticated' : 'loading';
  });

  /** Non-null only when BOTH the JWT user and the matching userDetails exist. */
  readonly session: Signal<Session | null> = computed(() => {
    const user = this.userSignal();
    const details = this.userState();
    return this.authStatus() === 'authenticated' && user && details ? { user, details } : null;
  });

  /** Replays the current session to late subscribers and emits on every userDetails update. */
  readonly session$: Observable<Session | null> = toObservable(this.session);

  /** Replays, but only emits on login, logout or user switch – not on profile edits or token refreshes. */
  readonly sessionChange$: Observable<Session | null> = this.session$.pipe(
    distinctUntilChanged((a, b) => (a?.user.id ?? null) === (b?.user.id ?? null))
  );

  /** Resolves once both objects exist. Never resolves for an anonymous visitor – check authStatus first if that matters. */
  whenAuthenticated(): Promise<Session> {
    return firstValueFrom(this.session$.pipe(filter((session): session is Session => !!session)));
  }

  set userDetails(value: UserObj) {
    this._userDetails = value;
    this.userState.set(value);

    if (this._userDetails?.tutorDetails) {
      this._userDetails.tutorDetails = { ...this._userDetails.tutorDetails, _canTeachOnline: this._userDetails.tutorDetails.canTeachOnline === "true", _acceptsNewStudents: this._userDetails.tutorDetails.acceptsNewStudents === "true", _offersAtHomeLessons: String(this._userDetails.tutorDetails.offersAtHomeLessons) !== "false" }
    }
    if (this._userDetails?.studentDetails && !this._userDetails.studentDetails.parentInfo) {
      this._userDetails.studentDetails.parentInfo = {};
    }
    sessionStorage?.setItem('userDetails', JSON.stringify(this._userDetails));
  };

  redirectToTutorRegistration() {
    if (!this.location.path().includes('reg2')) {
      this.router.navigate(['reg2']);
    }
  }

  get userDetails(): UserObj {
    return this._userDetails;
  }

  anonymousId: string;

  @Output() onCurrentUserChange = new EventEmitter<UserObj>();

  private _userDetails: UserObj;
  private canRefresh = true;
  private refreshed = new Subject<string | null>();

  constructor(
    private apiService: ApiService,
    private chatService: ChatService,
    private cookieService: CustomCookieServiceService,
    private httpClient: HttpClient,
    private location: Location,
    private router: Router,
    private translationService: TranslateService,
    private utilityService: UtilityService,
    private websocketService: WebsocketService,
  ) {
    if (this.utilityService.isBrowser()) {
      this.setUserFromAccessToken();
    }
  }

  get isAdminImpersonating(): boolean {
    return !!this.user?.adminId;
  }

  get isSessionActive(): boolean {
    return !!this.cookieService.get('access');
  }

  get isAuthenticated(): Observable<boolean | string> {
    return this.user ? of(true) : this.refresh();
  }

  setUserFromAccessToken(accessToken?: string): void {
    accessToken = accessToken ? accessToken : this.cookieService.get('access');
    if (accessToken) {
      this.setUser(accessToken);
    } else {
      sessionStorage.removeItem('userDetails')
      localStorage.removeItem('userId')
      let _anonymousId = localStorage?.getItem('anonymousId');
      if (!_anonymousId) {
        _anonymousId = this.utilityService.generateUuid();
        localStorage?.setItem('anonymousId', _anonymousId);
      }
      this.anonymousId = _anonymousId;
    }
  }

  updatePassword(password) {
    return this.httpClient.put(`${this.utilityService.apiBase}/auth/user/${this.user.id}/password`, { 'password': password })
  }

  getUserDetails() {
    let route = `${this.utilityService.apiBase}/user/${this.user.id}?cacheBust=${Date.now()}`
    return this.httpClient.get<UserObj>(route).pipe(tap(resp => {
      this.userDetails = resp;
    }))
  }

  //NOTE: send only the updated fields + id
  // By default a failed PUT completes silently (EMPTY) because most callers fire-and-forget. Pass
  // `propagateErrors: true` when the caller must know (e.g. a save that gates a follow-up request), and `headers`
  // to bypass the global error toast when the caller shows its own.
  updateUserDetails(user: UserObj, options: { propagateErrors?: boolean; headers?: HttpHeaders } = {}): Observable<UserObj> {
    return this.httpClient.put<UserObj>(`${this.utilityService.apiBase}/user/${this.user.id}`, user, { headers: options.headers }).pipe(
      catchError(error => (options.propagateErrors ? throwError(() => error) : EMPTY)),
      tap(resp => {
        this.userDetails = resp
      })
    )
  }

  login(email: string, password: string, recaptcha: string): Observable<{ access: string }> {
    const loginData = {
      email,
      password,
      captcha: recaptcha,
      country: this.utilityService.country
    };
    const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);

    return this.apiService.postWithHeaders(`auth/login`, loginData, headers).pipe(
      tap(response => {
        this.setUser(response.access ? response.access : '');
        this.associateAnonymousUserWithSearch();
      })
    );
  }

  redirectToRoleDashboard(): void {
    this.translationService.get([
      'ROUTES.Misc_dashboard',
      'ROUTES.Misc_Dashboard_Student',
      'ROUTES.Misc_Dashboard_Tutor'
    ]).subscribe(links => {
      const role = this.isStudent() ? links['ROUTES.Misc_Dashboard_Student'] : links['ROUTES.Misc_Dashboard_Tutor'];
      this.router.navigate([`/${links['ROUTES.Misc_dashboard']}/${role}`]);
    });
  }

  redirectAfterLogin(toDashboard: boolean): void {
    const params = new URLSearchParams(window.location.search);
    const returnUrl = params.get('returnUrl');
    if (returnUrl) {
      this.router.navigateByUrl(returnUrl);
    } else if (toDashboard) {
      this.redirectToRoleDashboard();
    }
  }

  loginWithGoogle() {
    this.httpClient.get(`${this.utilityService.apiBase}/auth/login/google/${this.utilityService.country}`).subscribe({
      next: (response: SocialSignInResponse) => {
        this.saveRouteOnGoogleAuth();
        window.location.href = response.url;
      }
    });
  }

  private saveRouteOnGoogleAuth() {
    let wasSet: boolean = true;
    try {
      const returnUrl = new URLSearchParams(window.location.search).get('returnUrl');
      localStorage.setItem('routeAfterGAlogin', returnUrl || this.router.url)
    } catch (error) {
      wasSet = false;
    }
    return wasSet;
  }

  validateEmailAddress(email: string, recaptcha: string): Observable<any> {
    const url = `${this.utilityService.apiBase}/user/mail/check`;

    const params = new HttpParams()
      .set('captcha', recaptcha)
      .set('email', email);

    const headers = this.utilityService.addHeaders(BypassAPIGlobalHandleEnums.All);
    const options = {
      params: params,
      headers: headers
    }

    return this.httpClient.get(url, options).pipe(
      map((response: EmailCheckResponse) => response)
    );
  }

  parseJwt(token: string): User {
    // base64url -> base64 for every occurrence, then decode the bytes as UTF-8 (names like "Jürgen")
    const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), char => char.charCodeAt(0));
    return JSON.parse(new TextDecoder().decode(bytes));
  }

  logout() {
    this.httpClient.get(`${this.utilityService.apiBase}/auth/logout`).subscribe((response) => {
      this.clearSession();
      this.onCurrentUserChange.emit(null)
      this.websocketService.close();
      this.router.navigateByUrl('/');
    });
  }

  clearSession() {
    this.cookieService.remove('access');
    this.cookieService.remove('refresh');
    this.cookieService.remove('refresh_possible');
    this.user = null;
    this.userDetails = null;
    if (this.utilityService.isBrowser()) {
      localStorage.removeItem('userId')
      sessionStorage.removeItem('userDetails');
    }
    this.userState.set(null);
    this.chatService.close();
  }

  private async setUser(access: string): Promise<void> {
    return new Promise(resolve => {
      this.user = this.parseJwt(access);
      if (this.utilityService.isBrowser()) {
        localStorage.setItem('userId', this.user.id.toString())
        this.chatService.init(this.user);
        const userDetails = JSON.parse(sessionStorage?.getItem('userDetails'));
        if (userDetails && userDetails.id == this.user.id) {
          this.userDetails = userDetails;
          return resolve()
        }
        setTimeout(() => {
          this.getUserDetails().subscribe({
            next: resp => {
              this.onCurrentUserChange.emit(resp);
              resolve();
            },
            // Settle anyway; authStatus stays 'loading' until userDetails are fetched successfully
            error: () => resolve()
          });
        });
      }
    })
  }

  refresh(): Observable<string | null> {
    if (this.refreshing()) {
      return this.refreshed.asObservable();
    } else if (!this.canRefresh || !this.cookieService.hasKey('refresh_possible')) {
      return of(null);
    }
    this.refreshing.set(true);
    return this.httpClient.get<{ access: string }>('/auth/refresh').pipe(
      tap(resp => {
        if (resp.access) {
          // Not awaited: waiters on `refreshed` only need the token. Awaiting userDetails here
          // deadlocks when that fetch itself gets a 401 and the interceptor waits on `refreshed`.
          // setUser sets the JWT user synchronously, so authStatus goes straight to 'loading'.
          this.setUser(resp.access);
          //  TODO THING IF THERE IS A REFRESH TOKEN WE MIGHT HAVE THE USERDETAILS IN LOCAL STORAGE
        }
        this.refreshing.set(false);
        this.refreshed.next(resp.access);
      }),
      map(resp => {
        return resp.access ?? null
      }),
      catchError((error) => {
        this.canRefresh = false;
        this.refreshing.set(false);
        this.refreshed.next(null);
        return of(null);
      })
    );
  }

  getUser() {
    return this.user;
  }

  isAdmin(): boolean {
    return this.user && this.user.role == UserRole.admin;
  }

  isStudent(): boolean {
    return this.user && this.user.role == UserRole.student;
  }

  isTeacher(): boolean {
    return this.user && this.user.role == UserRole.teacher;
  }

  isSignedIn(): boolean {
    return this.user && this.user.role > UserRole.signedout;
  }

  isCustomer(): boolean {
    return this.user && this.user.role > UserRole.signedout && this.user.role < UserRole.admin;
  }

  setImage(image: string) {
    this.user.image = image;
  }

  private associateAnonymousUserWithSearch() {
    this.apiService.put(`user/search-history/${this.anonymousId}/associate`, {}).subscribe();
  }

}